'use client';
import React from 'react';
import Image from 'next/image';
import Link from 'next/link';
import PhoneLink from '@/app/contact/PhoneLink';

export default function PrivacyPolicyPage() {
  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@700;800&family=Barlow:wght@400;500;600&display=swap');

        .pp-body {
          background: #0d0f12;
          color: #c8cdd9;
          font-family: 'Barlow', sans-serif;
          font-size: 16px;
          line-height: 1.75;
          -webkit-font-smoothing: antialiased;
          min-height: 100vh;
          position: relative;
        }

        .pp-body::before {
          content: '';
          position: fixed;
          inset: 0;
          background:
            repeating-linear-gradient(90deg, transparent, transparent 40px, rgba(255,255,255,.012) 40px, rgba(255,255,255,.012) 41px),
            repeating-linear-gradient(0deg, transparent, transparent 40px, rgba(255,255,255,.012) 40px, rgba(255,255,255,.012) 41px);
          pointer-events: none;
          z-index: 0;
        }

        .pp-header {
          position: relative;
          z-index: 1;
          border-bottom: 1px solid #2e3545;
          background: #1a1e26;
          padding: 18px 24px;
          display: flex;
          align-items: center;
          gap: 16px;
        }

        .pp-logo-text {
          font-family: 'Barlow Condensed', sans-serif;
          font-weight: 800;
          font-size: 1.1rem;
          letter-spacing: .05em;
          text-transform: uppercase;
          color: #f4f6fa;
          line-height: 1.1;
        }

        .pp-logo-text span { color: #f97316; }

        .pp-header-tag {
          margin-left: auto;
          font-size: .75rem;
          letter-spacing: .08em;
          text-transform: uppercase;
          color: #8a93a8;
          font-family: 'Barlow Condensed', sans-serif;
          font-weight: 700;
        }

        .pp-page {
          position: relative;
          z-index: 1;
          max-width: 780px;
          margin: 0 auto;
          padding: 60px 24px 80px;
        }

        .pp-title {
          font-family: 'Barlow Condensed', sans-serif;
          font-weight: 800;
          font-size: clamp(2rem, 5vw, 3rem);
          text-transform: uppercase;
          letter-spacing: .03em;
          color: #f4f6fa;
          line-height: 1;
          margin-bottom: 8px;
        }

        .pp-title em {
          font-style: normal;
          color: #f97316;
        }

        .pp-last-updated {
          font-size: .8rem;
          color: #8a93a8;
          margin-bottom: 48px;
          letter-spacing: .04em;
        }

        .pp-section {
          margin-bottom: 44px;
          padding-bottom: 44px;
          border-bottom: 1px solid #2e3545;
        }

        .pp-section:last-of-type {
          border-bottom: none;
        }

        .pp-h2 {
          font-family: 'Barlow Condensed', sans-serif;
          font-weight: 700;
          font-size: 1.3rem;
          text-transform: uppercase;
          letter-spacing: .06em;
          color: #f4f6fa;
          margin-bottom: 16px;
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .pp-h2::before {
          content: '';
          display: block;
          width: 3px;
          height: 18px;
          background: #f97316;
          border-radius: 2px;
          flex-shrink: 0;
        }

        .pp-p { margin-bottom: 14px; }
        .pp-p:last-child { margin-bottom: 0; }

        .pp-ul {
          padding-left: 20px;
          margin-bottom: 14px;
        }

        .pp-ul li {
          margin-bottom: 8px;
        }

        .pp-highlight {
          background: rgba(249,115,22,.07);
          border: 1px solid rgba(249,115,22,.2);
          border-left: 3px solid #f97316;
          border-radius: 3px;
          padding: 16px 20px;
          margin: 20px 0;
          font-size: .93rem;
          color: #c8cdd9;
        }

        .pp-contact-card {
          background: #1a1e26;
          border: 1px solid #2e3545;
          border-top: 3px solid #f97316;
          border-radius: 4px;
          padding: 28px 32px;
          margin-top: 48px;
        }

        .pp-contact-card h3 {
          font-family: 'Barlow Condensed', sans-serif;
          font-weight: 700;
          font-size: 1rem;
          letter-spacing: .08em;
          text-transform: uppercase;
          color: #f97316;
          margin-bottom: 14px;
        }

        .pp-contact-card a {
          color: #f4f6fa;
          text-decoration: none;
          border-bottom: 1px solid #2e3545;
          transition: color .2s, border-color .2s;
        }

        .pp-contact-card a:hover {
          color: #f97316;
          border-color: #f97316;
        }

        .pp-footer {
          position: relative;
          z-index: 1;
          text-align: center;
          padding: 24px;
          border-top: 1px solid #2e3545;
          font-size: .8rem;
          color: #8a93a8;
        }

        .pp-link { color: #f97316; }

        @media (max-width: 480px) {
          .pp-page { padding: 40px 16px 60px; }
          .pp-contact-card { padding: 20px; }
        }
      `}</style>

      <div className="pp-body">
        {/* Header */}
        <header className="pp-header">
          <Link href="/" aria-label="Home" className="flex items-center">
            <Image
              src="/assets/images/compressed-1778646282485.jpg"
              alt="Northern Hood and Exhaust Logo"
              width={160}
              height={64}
              className="h-9 w-auto"
              priority
              style={{ mixBlendMode: 'multiply' }}
            />
          </Link>
          <span className="pp-header-tag">Legal</span>
        </header>

        {/* Content */}
        <main className="pp-page">
          <h1 className="pp-title">Privacy <em>Policy</em></h1>
          <p className="pp-last-updated">Last updated: May 27, 2025 &nbsp;·&nbsp; Northern Hood &amp; Exhaust Ltd. &nbsp;·&nbsp; Calgary, Alberta, Canada</p>

          {/* 1 */}
          <div className="pp-section">
            <h2 className="pp-h2">Who We Are</h2>
            <p className="pp-p">Northern Hood &amp; Exhaust (&ldquo;we,&rdquo; &ldquo;our,&rdquo; or &ldquo;us&rdquo;) is a commercial kitchen exhaust cleaning and NFPA 96 compliance company operating in Calgary and the surrounding area, including Airdrie, Cochrane, Okotoks, and Chestermere, Alberta.</p>
            <p className="pp-p">This Privacy Policy explains how we collect, use, and protect personal information submitted through our website (<a href="https://northernhood.ca" className="pp-link">northernhood.ca</a>), Google lead forms, Meta lead forms, and any other digital touchpoints we operate.</p>
          </div>

          {/* 2 */}
          <div className="pp-section">
            <h2 className="pp-h2">Information We Collect</h2>
            <p className="pp-p">We collect personal information only when you voluntarily provide it to us. This may include:</p>
            <ul className="pp-ul">
              <li><strong>Contact details:</strong> Name, phone number, email address</li>
              <li><strong>Business information:</strong> Business name, address, city</li>
              <li><strong>Service inquiry details:</strong> Type of service needed, kitchen type, cleaning history</li>
              <li><strong>Technical data:</strong> IP address, browser type, pages visited, referral source (collected automatically via cookies and analytics tools)</li>
            </ul>
            <div className="pp-highlight">
              We do not collect payment card numbers, social insurance numbers, or any sensitive financial information through our website or lead forms.
            </div>
          </div>

          {/* 3 */}
          <div className="pp-section">
            <h2 className="pp-h2">How We Use Your Information</h2>
            <p className="pp-p">We use the information you provide to:</p>
            <ul className="pp-ul">
              <li>Respond to your quote request or service inquiry</li>
              <li>Schedule and confirm cleaning appointments</li>
              <li>Send compliance documentation and service reports</li>
              <li>Follow up on previous service (maintenance reminders)</li>
              <li>Send relevant service updates or promotions — only with your consent, in accordance with CASL</li>
              <li>Improve our website and advertising based on aggregated analytics data</li>
            </ul>
            <p className="pp-p">We will never sell, rent, or trade your personal information to third parties for their own marketing purposes.</p>
          </div>

          {/* 4 */}
          <div className="pp-section">
            <h2 className="pp-h2">Google Ads &amp; Lead Forms</h2>
            <p className="pp-p">We use Google Ads, including Google Lead Form Extensions, to generate service inquiries. When you submit a lead form served by Google, your information is transmitted to us through Google&apos;s platform and is governed by both this Privacy Policy and <a href="https://policies.google.com/privacy" target="_blank" rel="noopener" className="pp-link">Google&apos;s Privacy Policy</a>.</p>
            <p className="pp-p">We also use the Google Ads conversion tracking tag and Google Analytics 4 (GA4) on our website to measure the effectiveness of our advertising. These tools use cookies to collect anonymized data about website visits and actions taken. No personally identifiable information is shared with Google through these tools.</p>
            <p className="pp-p">You can opt out of Google&apos;s interest-based advertising at <a href="https://adssettings.google.com" target="_blank" rel="noopener" className="pp-link">adssettings.google.com</a>.</p>
          </div>

          {/* 5 */}
          <div className="pp-section">
            <h2 className="pp-h2">Meta (Facebook &amp; Instagram) Ads</h2>
            <p className="pp-p">We run advertising campaigns on Facebook and Instagram through Meta Platforms, Inc. We use the Meta Pixel on our website to measure ad performance and build relevant audiences. This tool may collect information about your visit and actions on our site.</p>
            <p className="pp-p">If you submit a lead form served through Facebook or Instagram, your information is handled under both this Privacy Policy and <a href="https://www.facebook.com/privacy/policy/" target="_blank" rel="noopener" className="pp-link">Meta&apos;s Privacy Policy</a>.</p>
            <p className="pp-p">You can manage your Meta ad preferences at <a href="https://www.facebook.com/adpreferences" target="_blank" rel="noopener" className="pp-link">facebook.com/adpreferences</a>.</p>
          </div>

          {/* 6 */}
          <div className="pp-section">
            <h2 className="pp-h2">Cookies</h2>
            <p className="pp-p">Our website uses cookies — small text files stored on your device — to improve your experience and measure website performance. Cookies we use include:</p>
            <ul className="pp-ul">
              <li><strong>Essential cookies:</strong> Required for the website to function</li>
              <li><strong>Analytics cookies:</strong> Google Analytics (anonymized traffic data)</li>
              <li><strong>Advertising cookies:</strong> Google Ads and Meta Pixel (conversion and remarketing tracking)</li>
            </ul>
            <p className="pp-p">You can disable cookies through your browser settings at any time. Note that disabling cookies may affect website functionality.</p>
          </div>

          {/* 7 */}
          <div className="pp-section">
            <h2 className="pp-h2">CASL Compliance</h2>
            <p className="pp-p">We comply with Canada&apos;s Anti-Spam Legislation (CASL). We will only send you commercial electronic messages (emails, SMS) if you have provided express or implied consent as defined under CASL.</p>
            <p className="pp-p">Every marketing message we send will include a clear and easy way to unsubscribe. You can withdraw consent at any time by contacting us at <a href="mailto:sales@northernhood.ca" className="pp-link">sales@northernhood.ca</a>.</p>
          </div>

          {/* 8 */}
          <div className="pp-section">
            <h2 className="pp-h2">Data Retention</h2>
            <p className="pp-p">We retain your personal information for as long as is necessary to provide services to you, maintain business records as required by law (including NFPA 96 compliance documentation), and resolve any disputes.</p>
            <p className="pp-p">If you request that we delete your information, we will do so within 30 days, except where retention is required by law.</p>
          </div>

          {/* 9 */}
          <div className="pp-section">
            <h2 className="pp-h2">Your Rights</h2>
            <p className="pp-p">Under Canada&apos;s Personal Information Protection and Electronic Documents Act (PIPEDA) and Alberta&apos;s Personal Information Protection Act (PIPA), you have the right to:</p>
            <ul className="pp-ul">
              <li>Access the personal information we hold about you</li>
              <li>Request correction of inaccurate information</li>
              <li>Withdraw consent for marketing communications</li>
              <li>Request deletion of your personal information (subject to legal retention requirements)</li>
            </ul>
            <p className="pp-p">To exercise any of these rights, contact us at the details below.</p>
          </div>

          {/* 10 */}
          <div className="pp-section">
            <h2 className="pp-h2">Changes to This Policy</h2>
            <p className="pp-p">We may update this Privacy Policy from time to time. The date at the top of this page reflects the most recent revision. Continued use of our website or services after any update constitutes acceptance of the revised policy.</p>
          </div>

          {/* Contact Card */}
          <div className="pp-contact-card">
            <h3>Privacy Questions? Contact Us</h3>
            <p className="pp-p"><strong style={{ color: '#f4f6fa' }}>Northern Hood &amp; Exhaust</strong><br />Calgary, Alberta, Canada</p>
            <p className="pp-p">
              Email: <a href="mailto:sales@northernhood.ca">sales@northernhood.ca</a><br />
              Phone: <PhoneLink source="/privacy-policy — phone link 1">(587) 990-7769</PhoneLink><br />
              Web: <a href="https://northernhood.ca">northernhood.ca</a>
            </p>
          </div>
        </main>

        <footer className="pp-footer">
          &copy; 2025 Northern Hood &amp; Exhaust Ltd. · Calgary, AB · All rights reserved.<br />
          <a href="https://northernhood.ca" className="pp-link" style={{ textDecoration: 'none' }}>northernhood.ca</a>
        </footer>
      </div>
    </>
  );
}
